<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

interface SelectItem {
  id: string
  name: string
  children?: SelectItem[]
  highlightedName?: string
}

interface Props {
  items: SelectItem[]
  modelValue: string | null
  placeholder?: string
  searchPlaceholder?: string
  label?: string
  disabled?: boolean
  error?: boolean
  required?: boolean
  readonly?: boolean
  loading?: boolean
  hasMore?: boolean
  searchDebounceMs?: number
  loadMoreThreshold?: number
}

interface Emits {
  (e: 'update:modelValue', value: string | null): void
  (e: 'change', value: string | null): void
  (e: 'search', value: string): void
  (e: 'load-more'): void
  (e: 'open'): void
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  label: '',
  disabled: false,
  loading: false,
  hasMore: false,
  searchDebounceMs: 300,
  loadMoreThreshold: 48,
})

const emit = defineEmits<Emits>()

const selectContainer = ref<HTMLElement>()
const listContainer = ref<HTMLElement>()

const search = ref('')
const searchDebounced = ref('')
const isOpen = ref(false)
const selectedItem = ref<SelectItem | null>(null)
const dropdownPosition = ref({ top: 0, left: 0, width: 0 })
const openedTreeNodes = ref<string[]>([])
const maxDropdownHeight = 400
const viewportGap = 12

let searchTimeout: ReturnType<typeof setTimeout> | null = null

const highlightMatch = (text: string, searchTerm: string) => {
  if (!searchTerm || !text) {
    return text
  }
  const safe = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')

  return text.replace(re, '<mark class="search-highlight">$1</mark>')
}

const resetSearch = () => {
  search.value = ''
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  searchDebounced.value = ''
}

const hasTreeItems = computed(() =>
  props.items.some(item => Array.isArray(item.children) && item.children.length > 0),
)

const filteredItems = computed(() => {
  if (hasTreeItems.value) {
    return []
  }

  if (!searchDebounced.value) {
    return props.items
  }

  return props.items
    .filter(item =>
      item.name.toLowerCase().includes(searchDebounced.value.toLowerCase()),
    )
    .map(item => ({
      ...item,
      highlightedName: highlightMatch(item.name, searchDebounced.value),
    }))
})

const filterTreeItems = (items: SelectItem[], searchTerm: string): SelectItem[] => {
  if (!searchTerm) {
    return items
  }

  return items.reduce<SelectItem[]>((acc, item) => {
    const children = Array.isArray(item.children) ? item.children : []
    const filteredChildren = filterTreeItems(children, searchTerm)
    const isMatched = item.name.toLowerCase().includes(searchTerm.toLowerCase())

    if (isMatched || filteredChildren.length) {
      acc.push({
        ...item,
        highlightedName: highlightMatch(item.name, searchTerm),
        children: filteredChildren,
      })
    }

    return acc
  }, [])
}

const filteredTreeItems = computed(() =>
  hasTreeItems.value ? filterTreeItems(props.items, searchDebounced.value) : [],
)

interface FlatNode extends SelectItem {
  depth: number
  isLeaf: boolean
}

const flattenTree = (items: SelectItem[], depth: number, isSearching: boolean): FlatNode[] =>
  items.reduce<FlatNode[]>((acc, item) => {
    const hasChildren = Boolean(item.children?.length)
    acc.push({ ...item, depth, isLeaf: !hasChildren })
    if (hasChildren && (isSearching || openedTreeNodes.value.includes(item.id))) {
      acc.push(...flattenTree(item.children!, depth + 1, isSearching))
    }
    return acc
  }, [])

const visibleTreeNodes = computed(() =>
  flattenTree(filteredTreeItems.value, 0, Boolean(searchDebounced.value)),
)

const collectParentNodeIds = (items: SelectItem[]): string[] => {
  return items.reduce<string[]>((acc, item) => {
    const children = Array.isArray(item.children) ? item.children : []
    if (children.length > 0) {
      acc.push(item.id, ...collectParentNodeIds(children))
    }
    return acc
  }, [])
}

const findItemById = (items: SelectItem[], id: string): SelectItem | null => {
  for (const item of items) {
    if (item.id === id) {
      return item
    }
    if (item.children?.length) {
      const found = findItemById(item.children, id)
      if (found) {
        return found
      }
    }
  }

  return null
}

const handleSelect = (item: SelectItem) => {
  selectedItem.value = item
  emit('update:modelValue', item.id)
  emit('change', item.id)
  isOpen.value = false
  emit('close')
  resetSearch()
}

const toggleTreeNode = (id: string) => {
  const idx = openedTreeNodes.value.indexOf(id)
  if (idx === -1) {
    openedTreeNodes.value.push(id)
  } else {
    openedTreeNodes.value.splice(idx, 1)
  }
}

const clearSelection = () => {
  selectedItem.value = null
  emit('update:modelValue', null)
  emit('change', null)
}

const updateDropdownPosition = () => {
  if (selectContainer.value) {
    const rect = selectContainer.value.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - viewportGap
    const spaceAbove = rect.top - viewportGap
    const canOpenDown = spaceBelow >= Math.min(maxDropdownHeight, 220)
    const shouldOpenUp = !canOpenDown && spaceAbove > spaceBelow
    const top = shouldOpenUp
      ? rect.top + window.scrollY - Math.min(maxDropdownHeight, Math.max(spaceAbove, 180))
      : rect.bottom + window.scrollY

    dropdownPosition.value = {
      top: Math.max(viewportGap + window.scrollY, top),
      left: rect.left + window.scrollX,
      width: rect.width,
    }
  }
}

const toggleMenu = async () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    emit('open')
    await nextTick()
    if (hasTreeItems.value) {
      openedTreeNodes.value = collectParentNodeIds(props.items)
    }
    updateDropdownPosition()
  } else {
    emit('close')
  }
}

const onClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.custom-select') && !target.closest('.select-dropdown')) {
    isOpen.value = false
    emit('close')
    resetSearch()
  }
}

const emitLoadMoreIfNeeded = () => {
  if (!listContainer.value || !props.hasMore || props.loading) {
    return
  }

  const el = listContainer.value
  const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight

  if (distanceToBottom <= props.loadMoreThreshold) {
    emit('load-more')
  }
}

watch(isOpen, newVal => {
  if (newVal) {
    nextTick(updateDropdownPosition)
  }
})

watch(search, newSearch => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    searchDebounced.value = newSearch
    if (hasTreeItems.value && newSearch.trim()) {
      openedTreeNodes.value = collectParentNodeIds(filteredTreeItems.value)
    }
    emit('search', newSearch.trim())
  }, props.searchDebounceMs)
})

watch(
  [() => props.modelValue, () => props.items],
  ([newVal]) => {
    if (newVal) {
      const foundItem = findItemById(props.items, String(newVal))
      if (foundItem) {
        selectedItem.value = foundItem
        return
      }

      if (selectedItem.value && String(selectedItem.value.id) === String(newVal)) {}
    } else {
      selectedItem.value = null
    }
  },
  { immediate: true },
)

onMounted(() => {
  document.addEventListener('click', onClickOutside)
  window.addEventListener('scroll', updateDropdownPosition, true)
  window.addEventListener('resize', updateDropdownPosition)
})

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside)
  window.removeEventListener('scroll', updateDropdownPosition, true)
  window.removeEventListener('resize', updateDropdownPosition)

  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})
</script>

<template>
  <div
    ref="selectContainer"
    class="custom-select"
  >
    <VTextField
      :model-value="selectedItem?.name || ''"
      variant="outlined"
      :disabled="disabled"
      :placeholder="placeholder"
      persistent-placeholder
      clearable
      :error="error"
      :readonly="readonly"
      class="cursor-pointer invisible-readonly"
      @click:clear="clearSelection"
      @click.stop="toggleMenu"
    >
      <template #label>
        <span class="required-text">{{ label }}<sup
          v-if="required"
          class="required-label"
        >*</sup></span>
      </template>

      <template #append-inner>
        <VIcon
          :icon="isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
          size="20"
          @click.stop="toggleMenu"
        />
      </template>
    </VTextField>

    <Teleport to="body">
      <VCard
        v-if="isOpen"
        class="select-dropdown"
        :style="{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
        }"
      >
        <VSheet class="pa-4">
          <VTextField
            v-model="search"
            variant="outlined"
            clearable
            :placeholder="searchPlaceholder || placeholder"
            :append-inner-icon="search ? undefined : 'ri-search-line'"
            autofocus
            @click:clear="resetSearch"
            @click.stop
          />
        </VSheet>

        <div
          ref="listContainer"
          class="items-list-container"
          @scroll.passive="emitLoadMoreIfNeeded"
        >
          <div
            v-if="hasTreeItems"
            class="tree-items-list px-2 py-1"
          >
            <div
              v-for="node in visibleTreeNodes"
              :key="node.id"
              class="tree-node"
              :style="{ paddingLeft: `${node.depth * 20 + 8}px` }"
              @click="handleSelect(node)"
            >
              <VIcon
                v-if="!node.isLeaf"
                :icon="openedTreeNodes.includes(node.id) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"
                size="18"
                class="tree-node__icon"
                @click.stop="toggleTreeNode(node.id)"
              />
              <span
                v-else
                class="tree-node__icon tree-node__icon--spacer"
              />
              <span
                class="tree-node__label"
                v-html="node.highlightedName || node.name"
              />
            </div>
          </div>

          <VList
            v-else
            class="items-list"
          >
            <VListItem
              v-for="item in filteredItems"
              :key="item.id"
              class="select-item"
              @click="handleSelect(item)"
            >
              <VListItemTitle>
                <span v-html="item.highlightedName || item.name" />
              </VListItemTitle>
            </VListItem>

            <VListItem
              v-if="loading"
              class="select-item loading-row"
            >
              <VListItemTitle class="d-flex align-center justify-center">
                <AnimatedLogoLoader
                  :size="20"
                  class="mr-2"
                />
                <span>Загрузка...</span>
              </VListItemTitle>
            </VListItem>

            <VListItem
              v-else-if="!filteredItems.length"
              class="select-item empty-row"
            >
              <VListItemTitle class="text-medium-emphasis">
                Ничего не найдено
              </VListItemTitle>
            </VListItem>
          </VList>
        </div>
      </VCard>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
@use '../styles/journalFormFields' as jf;

.custom-select {
  @include jf.select-field-height;
  width: 100%;
  position: relative;
}

.select-dropdown {
  position: fixed;
  z-index: 10000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: min(400px, calc(100vh - 24px));
  display: flex;
  flex-direction: column;
}

.items-list-container {
  max-height: 300px;
  overflow-y: auto;
  flex: 1;
}

.items-list {
  padding: 0;
}

.tree-items-list {
  padding: 4px 0;
}

.tree-node {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding-top: 6px;
  padding-bottom: 6px;
  padding-right: 16px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.4;

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }

  &__icon {
    flex-shrink: 0;
    margin-right: 4px;

    &--spacer {
      display: inline-block;
      width: 18px;
      height: 18px;
    }
  }

  &__label {
    flex: 1;
    white-space: normal;
  }
}

.select-item {
  padding: 8px 16px;
  min-height: 48px;
  cursor: pointer;

  .v-list-item-title {
    font-size: 0.875rem;
    white-space: normal;
    line-height: 1.4;
  }

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }
}

.loading-row,
.empty-row {
  cursor: default;

  &:hover {
    background-color: transparent;
  }
}

:deep(.search-highlight) {
  background-color: rgb(var(--v-theme-primary-500), var(--v-activated-opacity));
  padding: 2px 0;
  border-radius: 2px;
}

.required-text {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
}

.required-label {
  color: rgb(var(--v-theme-error));
}
</style>
